'use client';

import { useRef } from 'react';
import Link from 'next/link';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HeaderLanding from '@/components/headerlanding';
import BerryCluster from '@/components/brand/BerryCluster';
import { bookDemoHref } from '@/lib/links';

gsap.registerPlugin(ScrollTrigger);
if (typeof window !== 'undefined') {
  gsap.registerPlugin(useGSAP);
}

export interface LandingFeature {
  title: string;
  description: string;
  bullets?: string[];
}

/** CTA id of a landing page, e.g. "Integrations & Security" -> landing_integrations_security. */
function landingCta(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `landing_${slug}`.slice(0, 80).replace(/_+$/, '');
}

export interface LandingChapterPageProps {
  title: string;
  subtitle: string;
  tagline?: string;
  heroDescription?: string;
  features: LandingFeature[];
  demoUrl?: string;
  accentColor?: string;
}

export default function LandingChapterPage({
  title,
  subtitle,
  tagline,
  heroDescription,
  features,
  demoUrl = bookDemoHref(landingCta(title)),
  accentColor = '#6D28D9',
}: LandingChapterPageProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const featureEls = containerRef.current?.querySelectorAll('[data-feature]');
    featureEls?.forEach((el) => {
      gsap.fromTo(el,
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0,
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            end: 'top 50%',
            scrub: 0.8,
          },
        }
      );
    });
  }, { scope: containerRef });

  return (
    <div
      ref={containerRef}
      className="flex flex-col"
    >
      <HeaderLanding />

      {/* ── Hero ── */}
      <section
        className="relative min-h-[80vh] flex flex-col items-center justify-center overflow-hidden text-center px-6"
        style={{ backgroundColor: accentColor }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
        </div>

        <div className="relative z-10 space-y-8 max-w-3xl">
          {tagline && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="inline-block px-4 py-2 rounded-full bg-white/20 text-white/90 text-sm font-medium"
            >
              {tagline}
            </motion.span>
          )}

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="space-y-3"
          >
            <h1 className="text-5xl md:text-6xl font-semibold text-white tracking-tight">{title}</h1>
            <p className="text-xl md:text-2xl text-white/80 font-light">{subtitle}</p>
          </motion.div>

          {heroDescription && (
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.6 }}
              className="text-white/70 text-lg leading-relaxed max-w-xl mx-auto"
            >
              {heroDescription}
            </motion.p>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50"
        >
          <span className="text-xs tracking-widest uppercase font-light">Scroll</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </section>

      {/* ── Features ── */}
      <main>
        {features.map((feature, i) => (
          <section
            key={feature.title}
            data-feature
            className="py-24 md:py-32"
            style={{ backgroundColor: '#FBFAFF' }}
          >
            <div className="container mx-auto max-w-4xl px-6">
              <div className="grid md:grid-cols-[1fr_2fr] gap-12 items-start">
                <div>
                  <span
                    className="inline-block mb-3 px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                  >
                    {`0${i + 1}`}
                  </span>
                  <h2
                    className="text-2xl md:text-3xl font-semibold leading-tight"
                    style={{ color: accentColor }}
                  >
                    {feature.title}
                  </h2>
                </div>
                <div className="space-y-4">
                  <p className="text-gray-600 text-lg leading-relaxed">{feature.description}</p>
                  {feature.bullets && feature.bullets.length > 0 && (
                    <ul className="space-y-3 mt-4">
                      {feature.bullets.map(b => (
                        <li key={b} className="flex items-start gap-3">
                          <div
                            className="mt-1 h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${accentColor}20` }}
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke={accentColor}>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="text-gray-600">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </section>
        ))}
      </main>

      {/* ── CTA ── */}
      <section
        className="py-24 text-center px-6 overflow-hidden relative"
        style={{ backgroundColor: accentColor }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/3 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-lg mx-auto space-y-6"
        >
          <BerryCluster className="w-16 h-16 mx-auto" color="#ffffff" strokeWidth={2.5} />
          <h2 className="text-4xl font-semibold text-white">Ready to explore?</h2>
          <p className="text-white/70 text-lg">See {title} in action with a personalized demo.</p>
          <Link href={demoUrl}>
            <Button className="bg-white hover:bg-gray-50 rounded-xl px-8 py-5 text-lg shadow-xl mt-2" style={{ color: accentColor }}>
              Book a Demo <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
